import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const BU_STYLE = {
  AMI: { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  AMT: { color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4" },
};

function fmt(val) {
  const n = Number(val);
  if (!n || isNaN(n)) return "—";
  return n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtQty(val) {
  const n = Number(val);
  if (!n || isNaN(n)) return "—";
  return n.toLocaleString("en-AE", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function avgPrice(value, qty) {
  const v = Number(value), q = Number(qty);
  if (!v || !q || isNaN(v) || isNaN(q)) return null;
  return v / q;
}

export function PageSalesTargets({ myPerms = [], userEmail = "" }) {
  const showToast = useToast();
  const now = new Date();
  const [year, setYear]         = useState(now.getFullYear());
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [activeSp, setActiveSp] = useState(0);
  const [edits, setEdits]       = useState({});
  const [saving, setSaving]     = useState(false);

  const isAdmin = myPerms.includes("USER_MANAGE");

  useEffect(() => {
    setLoading(true);
    setEdits({});
    setActiveSp(0);
    api.fetchSalesTargets(year)
      .then(setData)
      .catch(e => showToast(e.message, "error"))
      .finally(() => setLoading(false));
  }, [year]);

  const sp = data[activeSp] ?? null;

  function editKey(email, cid, month, field) {
    return `${email}|${cid}|${month}|${field}`;
  }

  function getCellVal(email, cid, month, field, monthData) {
    const k = editKey(email, cid, month, field);
    if (k in edits) return edits[k];
    const raw = field === "v" ? monthData.targetValue : monthData.targetQty;
    return raw != null ? String(raw) : "";
  }

  function setCell(email, cid, month, field, value) {
    setEdits(prev => ({ ...prev, [editKey(email, cid, month, field)]: value }));
  }

  function colTotal(cid, field) {
    if (!sp) return null;
    const company = sp.companies.find(c => c.companyId === cid);
    if (!company) return null;
    let sum = 0;
    let hasAny = false;
    for (const m of company.months) {
      const raw = getCellVal(sp.userEmail, cid, m.month, field, m);
      const n = parseFloat(raw);
      if (!isNaN(n) && n > 0) { sum += n; hasAny = true; }
    }
    return hasAny ? sum : null;
  }

  async function handleSave() {
    if (!sp) return;
    setSaving(true);
    try {
      const targets = [];
      for (const company of sp.companies) {
        for (const m of company.months) {
          const vRaw = getCellVal(sp.userEmail, company.companyId, m.month, "v", m);
          const qRaw = getCellVal(sp.userEmail, company.companyId, m.month, "q", m);
          targets.push({
            userEmail: sp.userEmail,
            companyId: company.companyId,
            month:     m.month,
            targetValue: vRaw !== "" ? vRaw : null,
            targetQty:   qRaw !== "" ? qRaw : null,
          });
        }
      }
      await api.saveSalesTargets(year, targets);
      const fresh = await api.fetchSalesTargets(year);
      setData(fresh);
      setEdits({});
      showToast("Targets saved");
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    fontSize: 12, padding: "3px 6px", border: "1px solid #d1d5db",
    borderRadius: 3, width: 100, textAlign: "right", background: "white",
  };

  const thStyle = {
    padding: "8px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.05em", color: "#6b7280", background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb", textAlign: "right",
  };

  const tdStyle = (highlight) => ({
    padding: "7px 12px", fontSize: 12, borderBottom: "1px solid #f3f4f6",
    textAlign: "right", background: highlight ? "#fffbeb" : "white",
  });

  const totalRowStyle = {
    padding: "8px 12px", fontSize: 12, fontWeight: 700,
    textAlign: "right", background: "#f0fdf4", borderTop: "2px solid #bbf7d0",
  };

  return (
    <div style={{ padding: "20px 24px" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div className="page-header-left">
          <div className="page-title">Sales Targets</div>
          <div className="page-subtitle">Annual targets per salesperson — {year}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Year picker */}
          <button className="btn btn-secondary" onClick={() => setYear(y => y - 1)} style={{ padding: "5px 10px" }}>
            &lt;
          </button>
          <span style={{ fontWeight: 700, fontSize: 14, minWidth: 50, textAlign: "center" }}>{year}</span>
          <button className="btn btn-secondary" onClick={() => setYear(y => y + 1)} style={{ padding: "5px 10px" }}>
            &gt;
          </button>
          {isAdmin && (
            <button
              className="btn btn-primary"
              disabled={saving || !sp}
              onClick={handleSave}
              style={{ marginLeft: 8 }}
            >
              {saving ? "Saving…" : "Save Targets"}
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: 13 }}>Loading…</div>
      )}

      {!loading && data.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: 13 }}>
          No active salesperson assignments found.
        </div>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* Salesperson tabs */}
          <div style={{ display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap" }}>
            {data.map((s, i) => (
              <button
                key={s.userEmail}
                onClick={() => { setActiveSp(i); setEdits({}); }}
                style={{
                  padding: "6px 14px", borderRadius: 6, fontSize: 13, cursor: "pointer",
                  border: i === activeSp ? "2px solid #6d28d9" : "1px solid #d1d5db",
                  background: i === activeSp ? "#f5f3ff" : "white",
                  fontWeight: i === activeSp ? 700 : 400,
                  color: i === activeSp ? "#6d28d9" : "#374151",
                }}
              >
                {s.userName}
              </button>
            ))}
          </div>

          {/* Targets grid */}
          {sp && (
            <div className="card" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: "left", minWidth: 80 }}>Month</th>
                    {sp.companies.map(c => {
                      const s = BU_STYLE[c.businessUnit] ?? BU_STYLE.AMI;
                      const badge = (
                      <span style={{
                        color: s.color, background: s.bg,
                        border: `1px solid ${s.border}`,
                        borderRadius: 4, padding: "1px 6px", fontSize: 10,
                      }}>
                        {c.businessUnit}
                      </span>
                    );
                    return [
                        <th key={`${c.companyId}-v`} style={{ ...thStyle, minWidth: 130 }}>{badge}{" "}Value (AED)</th>,
                        <th key={`${c.companyId}-q`} style={{ ...thStyle, minWidth: 110 }}>{badge}{" "}Qty (Kg)</th>,
                        <th key={`${c.companyId}-a`} style={{ ...thStyle, minWidth: 110, color: "#9333ea" }}>{badge}{" "}Avg Price</th>,
                      ];
                    })}
                  </tr>
                </thead>
                <tbody>
                  {MONTHS.map((label, idx) => {
                    const m = idx + 1;
                    return (
                      <tr key={m}>
                        <td style={{ ...tdStyle(false), textAlign: "left", fontWeight: 600, color: "#374151" }}>
                          {label}
                        </td>
                        {sp.companies.map(c => {
                          const monthData = c.months[idx];
                          const vVal = getCellVal(sp.userEmail, c.companyId, m, "v", monthData);
                          const qVal = getCellVal(sp.userEmail, c.companyId, m, "q", monthData);
                          const avg = avgPrice(vVal, qVal);
                          return [
                            <td key={`${c.companyId}-v`} style={tdStyle(false)}>
                              {isAdmin
                                ? <input
                                    type="number" min="0" step="0.01"
                                    value={vVal}
                                    placeholder="AED"
                                    style={inputStyle}
                                    onChange={e => setCell(sp.userEmail, c.companyId, m, "v", e.target.value)}
                                  />
                                : <span style={{ color: vVal ? "#111827" : "#d1d5db", fontWeight: vVal ? 600 : 400 }}>
                                    {vVal ? `AED ${fmt(vVal)}` : "—"}
                                  </span>
                              }
                            </td>,
                            <td key={`${c.companyId}-q`} style={tdStyle(false)}>
                              {isAdmin
                                ? <input
                                    type="number" min="0" step="0.001"
                                    value={qVal}
                                    placeholder="Kg"
                                    style={inputStyle}
                                    onChange={e => setCell(sp.userEmail, c.companyId, m, "q", e.target.value)}
                                  />
                                : <span style={{ color: qVal ? "#111827" : "#d1d5db", fontWeight: qVal ? 600 : 400 }}>
                                    {qVal ? `${fmtQty(qVal)} Kg` : "—"}
                                  </span>
                              }
                            </td>,
                            <td key={`${c.companyId}-a`} style={{ ...tdStyle(false), color: avg ? "#7e22ce" : "#d1d5db", fontWeight: avg ? 600 : 400 }}>
                              {avg ? `AED ${fmt(avg)}/Kg` : "—"}
                            </td>,
                          ];
                        })}
                      </tr>
                    );
                  })}

                  {/* Totals row */}
                  <tr style={{ background: "#f0fdf4" }}>
                    <td style={{ ...totalRowStyle, textAlign: "left" }}>Total</td>
                    {sp.companies.map(c => {
                      const vTotal = colTotal(c.companyId, "v");
                      const qTotal = colTotal(c.companyId, "q");
                      const avgTotal = avgPrice(vTotal, qTotal);
                      return [
                        <td key={`${c.companyId}-v`} style={totalRowStyle}>
                          {vTotal != null ? `AED ${fmt(vTotal)}` : "—"}
                        </td>,
                        <td key={`${c.companyId}-q`} style={totalRowStyle}>
                          {qTotal != null ? `${fmtQty(qTotal)} Kg` : "—"}
                        </td>,
                        <td key={`${c.companyId}-a`} style={{ ...totalRowStyle, color: avgTotal ? "#7e22ce" : "#d1d5db" }}>
                          {avgTotal ? `AED ${fmt(avgTotal)}/Kg` : "—"}
                        </td>,
                      ];
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
